import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import noop from 'lodash/noop';

import {
  AdminApiAdminGlobalUserViewGetRequest,
  AdminApiAdminOnboardEmployeesPostRequest,
  AdminApiAdminV2FacilitiesGetRequest,
  Api,
  FacilitySetupRequest,
  FacilityUpdateRequest,
  useApi,
} from '@sbv-clients/data-access-api';
import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage, UnwrapArray } from '@sbv-clients/util-misc';

import { AdminCacheKeys } from '../utils/cache-keys';

export const useAdminFacilityIdGet = (facilityId: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [AdminCacheKeys.Facility, { facilityId }],
    queryFn: () =>
      api.adminApi.adminFacilityFacilityIdGet({
        facilityId,
      }),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useGlobalUsersGet = (
  body: AdminApiAdminGlobalUserViewGetRequest,
  config?: { keepPreviousData: boolean }
) => {
  const api = useApi();
  return useQuery({
    queryKey: [AdminCacheKeys.GlobalUsers, body],
    queryFn: () => api.adminApi.adminGlobalUserViewGet(body),
    retry: false,
    refetchOnWindowFocus: false,
    ...config,
  });
};

export const useAdminFacilityIdGetWithMutation = () => {
  const api = useApi();
  return useMutation((facilityId: string) =>
    api.adminApi
      .adminFacilityFacilityIdGet({
        facilityId,
      })
      .then((response) => response?.data)
  );
};

export const useAdminFacilitySearchUsers = (
  facilityId: string,
  pageNumber: number,
  pageSize: number,
  apiFilterParams: Record<string, any>
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      AdminCacheKeys.FacilityUsers,
      {
        facilityId,
        pageNumber,
        pageSize,
        ...apiFilterParams,
      },
    ],
    queryFn: () =>
      api.adminApi.adminFacilitySearchUsersPost({
        searchUserRequest: {
          facilityID: facilityId,
          pageNumber,
          pageSize,
          ...apiFilterParams,
        },
      }),
    refetchOnWindowFocus: false,
  });
};

const adminResetPassword =
  (api: Api) => (lockUserAccountRequest: { userId: string }) => {
    return api.adminApi.adminResetPasswordPut({ lockUserAccountRequest });
  };
export const useAdminResetPassword = () => {
  const api = useApi();
  const { notifySuccess, notifyError } = useSnackBar();

  return useMutation(adminResetPassword(api), {
    onSuccess: async () => {
      notifySuccess('Password reset link sent successfully');
    },
    onError: async (error) => {
      notifyError('Unable to send password reset link');
    },
  });
};

const adminLockUserAccount =
  (api: Api) => (lockUserAccountRequest: { userId: string }) => {
    return api.adminApi.adminLockUserAccountPut({ lockUserAccountRequest });
  };
export const useAdminLockUserAccount = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useSnackBar();

  return useMutation(adminLockUserAccount(api), {
    onSuccess: async () => {
      notifySuccess('User locked successfully');
      await queryClient
        .invalidateQueries([AdminCacheKeys.FacilityUsers])
        .catch(noop);
    },
    onError: async () => {
      notifyError('Unable to lock user account');
    },
  });
};

const adminUnLockUserAccount =
  (api: Api) => (lockUserAccountRequest: { userId: string }) => {
    return api.adminApi.adminUnlockUserAccountPut({ lockUserAccountRequest });
  };
export const useAdminUnLockUserAccount = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useSnackBar();

  return useMutation(adminUnLockUserAccount(api), {
    onSuccess: async () => {
      notifySuccess('User unlocked successfully');
      await queryClient
        .invalidateQueries([AdminCacheKeys.FacilityUsers])
        .catch(noop);
    },
    onError: async () => {
      notifyError('Unable to unlock user account');
    },
  });
};

export const useFacilityRoles = (facilityId: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [AdminCacheKeys.FacilityRoles, { facilityId }],
    queryFn: () =>
      api.adminApi.adminFacilityFacilityIdRolesGet({
        facilityId,
      }),
    refetchOnWindowFocus: false,
  });
};

export const useAdminFacilitiesGet = (
  body: AdminApiAdminV2FacilitiesGetRequest,
  config?: { keepPreviousData: boolean }
) => {
  const api = useApi();
  return useQuery({
    queryKey: [AdminCacheKeys.Facility, body],
    queryFn: () =>
      api.adminApi
        .adminV2FacilitiesGet(body)
        .then((response) => response?.data),
    refetchOnWindowFocus: false,
    ...config,
  });
};

export type GetAdminFacilitiesGetData = ReturnType<
  typeof useAdminFacilitiesGet
>['data'];
export type FacilityType = UnwrapArray<
  NonNullable<NonNullable<UnwrapArray<GetAdminFacilitiesGetData>>['facilities']>
>;

const adminFacilityDelete = (api: Api) => (body: { facilityId: string }) => {
  const { facilityId } = body;
  return api.adminApi.adminFacilityFacilityIdDelete({ facilityId });
};

export const useAdminFacilityDelete = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  return useMutation(adminFacilityDelete(api), {
    onError: async () => {
      notifyError('There was a problem delete the facility.');
    },
  });
};

const updateAdminFacility =
  (api: Api) => (facilityUpdateRequest: FacilityUpdateRequest) => {
    const { facilityId } = facilityUpdateRequest;
    return api.adminApi.adminFacilityFacilityIdPut({
      facilityId,
      facilityUpdateRequest,
    });
  };

export const useUpdateAdminFacility = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(updateAdminFacility(api), {
    onSuccess: async () => {
      await queryClient.invalidateQueries([AdminCacheKeys.Facility]);
    },
    onError: async (error) => {
      notifyError(
        `We had a problem updating the facility: ${getApiErrorMessage(error)}`
      );
    },
  });
};

export const useAdminTimezoneGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [AdminCacheKeys.AdminTimezones],
    queryFn: () => api.adminApi.adminTimezonesGet(),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useAdminFacilityEmployeesGet = (
  facilityId: string | 'All',
  onlyNew: boolean
) => {
  const param = facilityId === 'All' ? null : facilityId;
  const api = useApi();
  return useQuery({
    queryKey: [
      AdminCacheKeys.FacilitiesEmployees,
      {
        facilityId,
        onlyNew,
      },
    ],
    queryFn: () =>
      api.adminReportsApi
        .adminReportsFacilityEmployeeAggregatesPost({
          reportFilterRequest: {
            facilityId: param,
            onlyShowNewScheduler: onlyNew,
          },
        })
        .then((d) => d.data),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

const onboardAdminFacility =
  (api: Api) => (facilitySetupRequest: FacilitySetupRequest) => {
    return api.adminApi.adminOnboardFacilityPost({ facilitySetupRequest });
  };

export const useOnboardAdminFacility = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(onboardAdminFacility(api), {
    onSuccess: async () => {
      await queryClient
        .invalidateQueries([AdminCacheKeys.Facility])
        .catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

export const useAdminOnboardEmployees = () => {
  const api = useApi();
  const { notifySuccess, notifyError } = useSnackBar();
  return useMutation(
    (body: AdminApiAdminOnboardEmployeesPostRequest) =>
      api.adminApi.adminOnboardEmployeesPost(body),
    {
      onSuccess: async () => {
        notifySuccess('SMS sent successfully');
      },
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useMinRequiredMobileVersionGet = (config?: {
  enabled?: boolean;
}) => {
  const api = useApi();
  return useQuery({
    queryKey: [AdminCacheKeys.MinRequiredMobileVersion],
    queryFn: () =>
      api.mobileAppVersionApi
        .apiV2MobileAppVersionMinRequiredGet()
        .then((response) => response?.data),
    refetchOnWindowFocus: false,
    ...config,
  });
};

const updateMinRequiredMobileVersion =
  (api: Api) => (minRequiredVersion: string) =>
    api.mobileAppVersionApi
      .apiV2MobileAppVersionMinRequiredPut({
        updateMinRequiredMobileVersionRequest: { minRequiredVersion },
      })
      .then((response) => response?.data);

export const useUpdateMinRequiredMobileVersion = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useSnackBar();

  return useMutation(updateMinRequiredMobileVersion(api), {
    onSuccess: (data) => {
      queryClient.setQueryData([AdminCacheKeys.MinRequiredMobileVersion], data);
      notifySuccess(
        `Minimum required version set to ${data?.minRequiredVersion}`
      );
    },
    onError: (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};
